import { Button } from "@/components/ui/Button";
import { usePayment } from "@/hooks/usePayment";
import * as shared from "@/styles/shared";
import { PaymentFlowStatus } from "@shared/types/payment";
import { CheckCircle2, CreditCard, Loader2, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./InvoiceList.styles";
import { InvoiceRow } from "./InvoiceRow";

const InvoiceList = () => {
  const { t } = useTranslation("payment");
  const payment = usePayment();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!payment.pendingInvoices.length || loadedRef.current) return;
    loadedRef.current = true;
    setSelectedIds(new Set(payment.pendingInvoices.map((inv) => inv.id)));
  }, [payment.pendingInvoices]);

  const toggleInvoice = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(payment.pendingInvoices.map((inv) => inv.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const selectedInvoices = useMemo(() => payment.pendingInvoices.filter((inv) => selectedIds.has(inv.id)), [payment.pendingInvoices, selectedIds]);
  const allSelected = !!payment.pendingInvoices.length && selectedIds.size === payment.pendingInvoices.length;

  const handlePaySelected = () => {
    const invoiceIds = selectedInvoices.map((inv) => inv.id);
    payment.executePayment(invoiceIds);
  };

  return (
    <>
      {payment.paymentStatus !== PaymentFlowStatus.PROCESSING && (
        <div className={styles.invoiceListContainer}>
          <div className={styles.listHeader}>
            <span className={shared.titleSmSemibold}>{t("invoicesToPay")}</span>
            <div className={shared.rowGap1}>
              <Button variant="ghost" size="sm" onClick={allSelected ? deselectAll : selectAll} className={styles.selectAllBtn}>
                {allSelected ? (
                  <>
                    <XCircle className={shared.iconXs} />
                    {t("deselectAll")}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className={shared.iconXs} />
                    {t("selectAll")}
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className={styles.listBody}>
            {payment.pendingInvoices.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} checked={selectedIds.has(inv.id)} onToggle={() => toggleInvoice(inv.id)} />
            ))}
          </div>
        </div>
      )}

      {payment.paymentStatus !== PaymentFlowStatus.PROCESSING ? (
        <Button onClick={handlePaySelected} disabled={payment.paying || !selectedInvoices.length} variant="gradient" className={styles.payBtn}>
          <CreditCard className={styles.icon} />
          {t("confirmPay")}
        </Button>
      ) : (
        <div className={styles.processingState}>
          <Loader2 className={styles.loaderSpin} />
          <span className={shared.muted}>{t("progressDot", { count: payment.processedCount, total: payment.selectedCount })}</span>
          <Button onClick={payment.handleCancelPayment} size="sm" variant="ghost" className={styles.cancelBtn}>
            <XCircle className={styles.icon} />
            {t("cancelPayment")}
          </Button>
        </div>
      )}
    </>
  );
};

export default InvoiceList;
